/**
 * Report tab landing — R6.11 unified IA for reporting issues.
 * 3 entry cards: Power Outage (worked, links to existing map), Hazard (stub), Streetlight (stub).
 * Atlas: Report tab landing.
 */
"use client";

import Link from "next/link";
import { Zap, AlertTriangle, Lightbulb } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { REPORT_STUBS } from "@petci/shared-ui";
import { useT } from "@/lib/locale";

interface ReportEntry {
  slug: string;
  href: string;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  // The live outage card is report's OWN chrome → keyed here (D1). The stub cards
  // read the shared REPORT_STUBS catalog, whose customer-visible copy is the
  // single stub SSoT localized in W5a (D1 boundary) — so they carry the catalog's
  // *Key fields, never its English `name`/`description` (F-P2-1: consuming the
  // English fields rendered mixed-language card rows under es/ht).
  nameKey: string;
  descriptionKey: string;
  live: boolean;
}

/**
 * Slug lookup into the stub SSoT. Mirrors `featureStatus()`'s throw-on-unknown
 * contract — a renamed slug must fail loudly at module load, not render blank.
 */
function reportStub(slug: string) {
  const stub = REPORT_STUBS.find((s) => s.slug === slug);
  if (!stub?.nameKey || !stub.descriptionKey) {
    throw new Error(`report hub: stub "${slug}" is missing its locale keys`);
  }
  return stub as { nameKey: string; descriptionKey: string };
}

const REPORT_ENTRIES: ReportEntry[] = [
  {
    slug: "outage",
    href: "/outage/map",
    icon: Zap,
    iconColor: "text-brand-accent",
    iconBg: "bg-brand-accent/10",
    nameKey: "report.outageName",
    descriptionKey: "report.outageDescription",
    live: true,
  },
  {
    slug: "streetlight",
    href: "/report/streetlight",
    icon: Lightbulb,
    iconColor: "text-brand-primary-text",
    iconBg: "bg-brand-primary/10",
    ...reportStub("streetlight"),
    live: true,
  },
  {
    slug: "hazard",
    href: "/report/hazard",
    icon: AlertTriangle,
    iconColor: "text-semantic-error",
    iconBg: "bg-semantic-error/10",
    ...reportStub("hazard"),
    live: false,
  },
];

export default function ReportPage() {
  const t = useT();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-bold text-neutral-900">
          {t("report.title")}
        </h1>
        <p className="text-sm text-neutral-700 mt-1">
          {t("report.subtitle")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {REPORT_ENTRIES.map((entry) => {
          const Icon = entry.icon;
          const name = t(entry.nameKey);
          const description = t(entry.descriptionKey);
          return (
            <Link
              key={entry.slug}
              href={entry.href}
              className={`rounded-xl p-6 shadow-sm hover:shadow-card-hover transition-shadow duration-base group flex flex-col ${
                entry.live
                  ? "border-2 border-brand-primary bg-brand-primary/5"
                  : "border border-neutral-200 bg-white"
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span
                  className={`w-10 h-10 rounded-lg ${entry.iconBg} flex items-center justify-center`}
                >
                  <Icon size={20} className={entry.iconColor} aria-hidden />
                </span>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-[10px] font-medium ${
                    entry.live
                      ? "bg-semantic-success/10 text-semantic-success-text"
                      : "bg-brand-secondary/10 text-brand-secondary"
                  }`}
                >
                  {entry.live ? t("report.availableNow") : t("common.deliveryRoadmap")}
                </span>
              </div>
              <h2 className="text-base font-semibold text-neutral-900 mb-2 group-hover:text-brand-primary-text transition-colors duration-fast">
                {name}
              </h2>
              <p className="text-sm text-neutral-500 leading-relaxed flex-1">
                {description}
              </p>
              <span className="mt-4 inline-flex text-xs font-medium text-brand-primary-text group-hover:translate-x-0.5 transition-transform duration-fast">
                {entry.live ? t("report.reportNow") : t("report.viewDetails")}
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
